import { useState, useRef, useEffect } from 'react';

/**
 * Single-input combobox. Type to filter, click to select.
 *
 * Props:
 *   options     — [{ value, label }]
 *   value       — currently selected value ('' for none)
 *   onChange    — (value) => void
 *   placeholder — hint shown when nothing is selected
 *   emptyLabel  — text for the "clear" row at the top of the list
 */
export default function SearchableSelect({
  options = [],
  value,
  onChange,
  placeholder = 'Search…',
  emptyLabel = '— None —',
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onMouseDown(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  const selected = options.find(o => String(o.value) === String(value));

  const filtered = query.trim()
    ? options.filter(o => o.label.toLowerCase().includes(query.trim().toLowerCase()))
    : options;

  function select(val) {
    onChange(val);
    setQuery('');
    setOpen(false);
  }

  return (
    <div ref={ref} className="relative">
      <input
        className="input w-full"
        placeholder={selected ? '' : placeholder}
        value={open ? query : (selected?.label ?? '')}
        onFocus={() => { setQuery(''); setOpen(true); }}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onKeyDown={e => { if (e.key === 'Escape') { setOpen(false); setQuery(''); } }}
      />
      {open && (
        <ul className="absolute z-50 w-full bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-52 overflow-y-auto text-sm">
          <li
            className="px-3 py-2 cursor-pointer hover:bg-panel text-muted"
            onMouseDown={() => select('')}
          >
            {emptyLabel}
          </li>
          {filtered.map(o => (
            <li
              key={o.value}
              className={`px-3 py-2 cursor-pointer hover:bg-panel ${String(o.value) === String(value) ? 'font-medium text-navy' : ''}`}
              onMouseDown={() => select(o.value)}
            >
              {o.label}
            </li>
          ))}
          {filtered.length === 0 && (
            <li className="px-3 py-2 text-muted italic">No matches</li>
          )}
        </ul>
      )}
    </div>
  );
}
